import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTitle } from '@/lib/useTitle';
import DimensionLine from '@/components/DimensionLine';
import PropertyValueLeadForm from '@/components/PropertyValueLeadForm';

export const meta = {
  path: '/commercial-property-value-calculator',
  title: 'Commercial Property Value Calculator — Waterloo Region & Guelph',
  description:
    'Estimate a commercial property value from annual NOI and an assumed cap rate, then request a property-specific review from Pinnacle Commercial in Waterloo Region or Guelph.',
};

function money(n: number) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

export default function CommercialPropertyValueCalculator() {
  useTitle(meta.title, meta.description);

  const [noi, setNoi] = useState(120000);
  const [capRate, setCapRate] = useState(6.0);

  const result = useMemo(() => {
    if (!Number.isFinite(noi) || noi <= 0 || !Number.isFinite(capRate) || capRate <= 0) return null;

    const base = noi / (capRate / 100);
    const tighter = Math.max(0.25, capRate - 0.5);
    const wider = capRate + 0.5;

    return {
      base,
      high: noi / (tighter / 100),
      low: noi / (wider / 100),
      tighter,
      wider,
    };
  }, [noi, capRate]);

  return (
    <>
      <section className="border-b border-medline bg-grid-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <p className="annot">Owner tool — capitalization method</p>
          <h1 className="mt-4 max-w-4xl font-display text-[38px] font-semibold leading-[1.03] tracking-tight sm:text-[58px]">
            Commercial property value calculator.
          </h1>
          <p className="mt-6 max-w-3xl text-[17px] leading-relaxed text-annot">
            Translate annual net operating income and an assumed cap rate into an indicative value range. No market cap rate is invented for you — you control the assumption.
          </p>
        </div>
      </section>

      <section className="bg-sheet">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[1fr_1fr]">
          <div>
            <DimensionLine label="Inputs" className="mb-8" />

            <label className="block">
              <span className="annot">Annual NOI</span>
              <div className="mt-2 flex items-center border border-medline bg-white">
                <span className="px-4 font-mono text-[13px] text-annot">$</span>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={noi}
                  onChange={(e) => setNoi(Number(e.target.value))}
                  className="w-full bg-transparent px-2 py-4 text-[18px] outline-none"
                />
              </div>
            </label>

            <label className="mt-7 block">
              <span className="annot">Assumed cap rate</span>
              <div className="mt-2 flex items-center border border-medline bg-white">
                <input
                  type="number"
                  min="0.25"
                  max="25"
                  step="0.1"
                  value={capRate}
                  onChange={(e) => setCapRate(Number(e.target.value))}
                  className="w-full bg-transparent px-4 py-4 text-[18px] outline-none"
                />
                <span className="px-4 font-mono text-[13px] text-annot">%</span>
              </div>
            </label>

            <div className="mt-8 border-l-2 border-redline bg-redline-100/40 px-5 py-4">
              <p className="font-mono text-[10px] uppercase tracking-wider2 text-redline-600">Formula</p>
              <p className="mt-2 text-[14px] leading-relaxed text-graphite-600">
                Indicative value = annual NOI ÷ cap rate. A lower cap-rate assumption produces a higher indicated value; a higher cap-rate assumption produces a lower one.
              </p>
            </div>
          </div>

          <div>
            <DimensionLine label="Indicative output" className="mb-8" />

            {result ? (
              <div className="border border-medline bg-table">
                <div className="border-b border-hairline px-6 py-7">
                  <p className="annot">At {capRate.toFixed(1)}%</p>
                  <p className="mt-2 font-display text-[42px] leading-none sm:text-[52px]">{money(result.base)}</p>
                </div>

                <div className="grid gap-px bg-medline sm:grid-cols-2">
                  <div className="bg-sheet px-6 py-6">
                    <p className="annot">If cap rate were {result.tighter.toFixed(1)}%</p>
                    <p className="mt-2 font-display text-[25px]">{money(result.high)}</p>
                  </div>
                  <div className="bg-sheet px-6 py-6">
                    <p className="annot">If cap rate were {result.wider.toFixed(1)}%</p>
                    <p className="mt-2 font-display text-[25px]">{money(result.low)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-medline bg-table px-6 py-8 text-[15px] text-annot">Enter a positive NOI and cap rate.</div>
            )}

            <p className="mt-5 text-[13px] leading-relaxed text-annot">
              This is capitalization math, not an appraisal or a broker opinion of value. Lease quality and expiry, vacancy, property condition, zoning, redevelopment potential, location, financing conditions and comparable transactions can materially change market value.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#property-review" className="btn-primary">
                Request a property-specific review
              </a>
              <Link to="/commercial-property-valuation-waterloo-region-guelph" className="btn-ghost">
                How commercial value is reviewed
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section id="property-review" className="scroll-mt-24 border-t border-medline bg-table">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <DimensionLine label="From estimate to a real property" className="mb-8" />
            <h2 className="max-w-xl font-display text-[30px] leading-tight sm:text-[38px]">
              The cap-rate math is the easy part. The property-specific factors are where value moves.
            </h2>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-annot">
              Send the address and your calculator assumptions. That gives Pinnacle Commercial enough context to look at the actual asset rather than treating every building in Kitchener, Waterloo, Cambridge or Guelph as interchangeable.
            </p>
            <ul className="mt-7 space-y-3 text-[14px] leading-relaxed text-graphite-600">
              <li>• Lease terms, expiry dates and tenant quality</li>
              <li>• Vacancy, market rent and operating-cost recoveries</li>
              <li>• Property condition, zoning and redevelopment potential</li>
              <li>• Location and relevant comparable transactions</li>
            </ul>
          </div>

          <PropertyValueLeadForm noi={noi} capRate={capRate} indicatedValue={result?.base ?? null} />
        </div>
      </section>

      <section className="border-t border-medline">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Before using NOI" className="mb-9" />
          <div className="grid gap-8 md:grid-cols-3">
            <div>
              <h2 className="font-display text-[20px]">Use operating income</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-annot">NOI is generally income after normal property operating expenses, before mortgage debt service, income tax and depreciation.</p>
            </div>
            <div>
              <h2 className="font-display text-[20px]">Normalize unusual items</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-annot">One-time repairs, temporary vacancy and below- or above-market leases can distort a single-year NOI and deserve separate review.</p>
            </div>
            <div>
              <h2 className="font-display text-[20px]">Cap rate is an assumption</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-annot">The correct market cap rate is property-specific. The calculator deliberately does not pretend there is one universal rate for Kitchener, Waterloo, Cambridge or Guelph.</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
